import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductModel;
    image: ImageModel;
    useLargeImage?: boolean;
    onClick?: () => void;
    extendedStyles?: ProductPrimaryImageStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

export interface ProductPrimaryImageStyles {
    centeringWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    hiddenWrapper?: InjectableCss;
    /**
     * @deprecated Not used anymore
     */
    spinner?: LoadingSpinnerProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
}

export const productPrimaryImageStyles: ProductPrimaryImageStyles = {
    centeringWrapper: {
        css: css`
            min-height: 300px;
            margin: 10px 0;
            display: flex;
            align-items: center;
            justify-content: center;
            img {
                height: auto;
                max-width: 100%;
            }
        `,
    },
    image: {
        css: css`
            margin: auto;
            cursor: pointer;
        `,
    },
    img: {
        css: css`
            cursor: pointer;
        `,
    },
};

const ProductPrimaryImage = ({ productSettings, product, image, useLargeImage, onClick, extendedStyles }: Props) => {
    const [styles] = useState(() => mergeToNew(productPrimaryImageStyles, extendedStyles));
    const [imgWidthHeight, setImgWidthHeight] = useState<{ width: string | number, height: string | number }>({ width: '', height: '' })
    const path = useLargeImage
        ? image.largeImagePath || image.mediumImagePath
        : image.mediumImagePath || image.largeImagePath;

    if (!path || !product) {
        return null;
    }

    useEffect(() => {
        if ((window as any).sirvScriptAdded) {
            return;
        }
        const image = new Image();
        image.src = path;
        image.onload = () => {
            setImgWidthHeight({
                width: image.width,
                height: image.height
            })
        };

        // Create a <link> element dynamically
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = path;
    
        // Append the link element to the <head> section
        document.head.appendChild(link);
    
        // Cleanup: Remove the <link> tag when the component is unmounted
        return () => {
          document.head.removeChild(link);
        };
    }, [path])

    useEffect(() => {
        if ((window as any).sirvScriptAdded) {
            return;
        }
        if (
            productSettings.imageProvider !== "SIRV" ||
            !product.images ||
            product.images.every(o => o.imageType !== "360")
        ) {
            return;
        }
        const script = document.createElement("script");
        script.src = "https://scripts.sirv.com/sirv.js";
        script.async = true;

        document.body.appendChild(script);
        (window as any).sirvScriptAdded = true;
    }, [productSettings.imageProvider, product]);

    const imageWrapperClickHandler = () => {
        onClick?.();
    };

    // const youtubeRegex = /^[a-zA-Z0-9_-]{11}$/;
    const youtubeRegex =
        /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?(?=.*v=\w+)(?:\S+)?|embed\/\w+|\S+\/\w+)|youtu\.be\/(\w+))(?:\S+)?$/;
    const videoRegex = /\.(mp4|mkv|avi|mov|wmv|flv|webm|mpg|mpeg|3gp|swf)$/;

    function isEmbedURL(url: string) {
        const embedDomains = ["youtube.com/watch", "www.youtube.com/watch"];

        return embedDomains.some(domain => url.includes(domain));
    }

    const imageWrapperKeyHandler: React.KeyboardEventHandler<HTMLImageElement> = event => {
        event.preventDefault();
        event.stopPropagation();
        if (event.key === "Enter") {
            onClick?.();
        }
    };


    return (
        <>
            {image.imageType === "Static" && (
                <StyledWrapper {...styles.centeringWrapper} onClick={imageWrapperClickHandler}>
                    {youtubeRegex.test(path) ? (
                        !isEmbedURL(path) ? (
                            <iframe
                                src={path}
                                width="100%"
                                height="550px"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                            ></iframe>
                        ) : null
                    ) : videoRegex.test(path) ? (
                        <StyledWrapper>
                            <video width="320" height="240" controls>
                                <source src={path} />
                                <track src="" kind="captions" label="English"></track>
                            </video>
                        </StyledWrapper>
                    ) : (
                        <StyledWrapper {...styles.centeringWrapper} onClick={imageWrapperClickHandler}>
                            <Img
                                {...styles.img}
                                src={path}
                                altText={image.imageAltText}
                                title={image.imageAltText}
                                loading="eager"
                                data-test-selector="productDetails_mainImage"
                                onKeyPress={imageWrapperKeyHandler}
                                tabIndex={0}
                                width={imgWidthHeight.width}
                                height={imgWidthHeight.height}
                            />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            )}
            {image.imageType === "360" && productSettings.imageProvider === "SIRV" && (
                <div style={{ minHeight: 300 }} onClick={imageWrapperClickHandler}>
                    <div className="Sirv" key={image.id} data-src={image.mediumImagePath} />
                </div>
            )}
        </>
    );
};

export default connect(mapStateToProps)(ProductPrimaryImage);
